/**
 * Long-lived browser id, stored in a first-party cookie.
 * Sent with a new booking so later appointments from the same browser can be grouped.
 * Never throws. A failure returns null and the booking payload is unchanged.
 */

const COOKIE_NAME = "fabs_persistent_id";
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

export function getPersistentId(): string | null {
    try {
        const existing = readCookie();
        if (existing) return existing;

        const id = generateId();
        writeCookie(id);
        return id;
    } catch {
        return null;
    }
}

function readCookie(): string | null {
    const parts = document.cookie ? document.cookie.split("; ") : [];
    for (let i = 0; i < parts.length; i++) {
        const eq = parts[i].indexOf("=");
        if (eq < 0) continue;
        if (parts[i].slice(0, eq) !== COOKIE_NAME) continue;
        const value = decodeURIComponent(parts[i].slice(eq + 1));
        return isGuid(value) ? value : null;
    }
    return null;
}

function writeCookie(id: string): void {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${COOKIE_NAME}=${id}; Max-Age=${ONE_YEAR_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

function isGuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

function generateId(): string {
    const c: any = typeof crypto !== "undefined" ? crypto : null;
    if (c && typeof c.randomUUID === "function") {
        return c.randomUUID();
    }
    if (c && typeof c.getRandomValues === "function") {
        const bytes = new Uint8Array(16);
        c.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        let hex = "";
        for (let i = 0; i < bytes.length; i++) {
            hex += (bytes[i] < 16 ? "0" : "") + bytes[i].toString(16);
        }
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
        const r = (Math.random() * 16) | 0;
        const v = ch === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
